'use client';

import { useState, useEffect, useRef } from 'react';

interface UserData {
  name: string;
  mobile: string;
  email: string;
  query: string;
  country: string;
  consentContact: boolean;
  marketingUpdates: boolean;
}

const baseUrl = process.env.NEXT_PUBLIC_BASE_URL

export default function ChatWidget() {
  // State management
  const [isChatVisible, setChatVisible] = useState(false);
  const [messages, setMessages] = useState<Array<{type: string, text: string, isTyping?: boolean}>>([]);
  const [inputValue, setInputValue] = useState('');
  const [conversationStage, setConversationStage] = useState('initial');
  const [showConsentOptions, setShowConsentOptions] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);

  const [userData, setUserData] = useState<UserData>({
    name: '',
    mobile: '',
    email: '',
    query: '',
    country: '',
    consentContact: false,
    marketingUpdates: false
  });
  
  // Refs
  const chatMessagesRef = useRef<HTMLDivElement>(null);
  const messageInputRef = useRef<HTMLTextAreaElement>(null);

  // Agent data
  const agent = {
    name: 'Aera from GenAIPulse',
    avatar: 'https://randomuser.me/api/portraits/women/68.jpg',
    company: 'GenAIPulse'
  };

  // Initial messages
 const initialMessages = [
  {
    type: 'agent',
    text: 'Hello! 👋 Thank you for reaching out. May I please know your full name?'
  }
];

const playNotificationSound = () => {
  if (audioRef.current) {
    audioRef.current.currentTime = 0; // Rewind to start
    audioRef.current.play().catch(e => console.log("Audio play failed:", e));
  }
};

  // Load initial messages when chat opens
  useEffect(() => {
    if (isChatVisible && messages.length === 0) {
      setMessages(initialMessages);
    }
  }, [isChatVisible]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (chatMessagesRef.current) {
      chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight;
    }
  }, [messages]);

  console.log(messages,"messages")

  useEffect(() => {
  if (messages.length > 0 && messages[messages.length - 1].type === 'agent') {
    playNotificationSound();
  }
}, [messages]);

  // Auto-open chat after 1 second
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!isChatVisible) {
        toggleChat();
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, []);

  const submitUserData = async (data: UserData) => {
    console.log(data,"data")
    try {
      const response = await fetch(`${baseUrl}/api/submitQuery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      if (!response.ok) {
        throw new Error('Failed to submit user data');
      }

      const result = await response.json();
      console.log('Data submitted successfully:', result);
      return result;
    } catch (error) {
      console.error('Error submitting user data:', error);
      throw error;
    }
  };

  // Toggle chat visibility
  const toggleChat = () => {
    setChatVisible(!isChatVisible);
  };

  // Add a new message
  const addMessage = (type: string, text: string) => {
    setMessages(prev => [...prev, { type, text }]);
  };

  // Validate email
  const validateEmailDetailed = (email: string) => {
    if (!email.includes('@')) {
      return "Email address must contain an '@' symbol.";
    }
    const parts = email.split('@');
    if (parts.length !== 2 || !parts[1].includes('.')) {
      return "Email address must include a domain (e.g., gmail.com).";
    }
    return null;
  };

  function validateMobileNumber(number: string) {
  const regex = /^\+?\d{7,15}$/; // Optional +, then 7–15 digits
  return regex.test(number) ? null : "Mobile number must be between 7 and 15 digits (optional + at start).";

}

  // Validate mobile number
  const validateMobileDetailed = (mobile: string) => {
    const cleanedMobile = mobile.replace(/^\+/, '').replace(/\D/g, '');
    
    if (cleanedMobile.length < 7 || cleanedMobile.length > 15) {
      return "Mobile number must be between 7 to 15 digits.";
    }
    if (!/^\+?\d{7,15}$/.test(mobile)) {
      return "Mobile number must contain only digits and may start with '+'.";
    }
    return null;
  };

  // Process user input
  const processUserInput = async (text: string) => {
    try {
      const response = await callOpenAI(text);
      return { text: response, type: 'agent' };
    } catch (error) {
      console.error('Error processing user input:', error);
      return { text: 'Sorry, I encountered an error processing your request.', type: 'agent' };
    }
  };

  // API simulation functions
  const callEmailAPI = (email: string) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Email API called with:', email);
        resolve({ success: true });
      }, 500);
    });
  };

  const callMobileAPI = (mobile: string) => {
    return new Promise((resolve) => {
      setTimeout(() => {
        console.log('Mobile API called with:', mobile);
        resolve({ success: true });
      }, 500);
    });
  };

  // Main send message function
const sendMessage = async () => {

    if (typeof window !== "undefined" && window.fbq) {
    window.fbq('track', 'ChatbotSubmit');
  }

  const message = inputValue.trim();
  if (!message) return;

  // Stage 1 - Name
  if (conversationStage === 'initial') {
    if (message.length < 4) {
      addMessage('system', "Could you please provide your full name?");
      return;
    }
    addMessage('user', message);
    setUserData(prev => ({ ...prev, name: message }));
    setInputValue('');
    addMessage('agent', `Nice to meet you, ${message}! Could you share your mobile number with country code?`);
    setConversationStage('collectMobile');

  // Stage 2 - Mobile Number
  } else if (conversationStage === 'collectMobile') {
    const mobileError = validateMobileNumber(message);
    if (mobileError) {
      addMessage('system', "That doesn't seem like a valid mobile number. Could you check and try again?");
      return;
    }
    addMessage('user', message);
    setUserData(prev => ({ ...prev, mobile: message }));
    setInputValue('');
    addMessage('agent', "Thanks! What's the best email address to reach you at?");
    setConversationStage('collectEmail');

  // Stage 3 - Email
  } else if (conversationStage === 'collectEmail') {
    const emailError = validateEmailDetailed(message);
    if (emailError) {
      addMessage('system', "That email doesn't look right. Could you double-check it?");
      return;
    }
    addMessage('user', message);
    setUserData(prev => ({ ...prev, email: message }));
    setInputValue('');
    addMessage('agent', "Great! Could you briefly describe how can I help you today?");
    setConversationStage('collectQuery');

  // Stage 4 - Query Text
  } else if (conversationStage === 'collectQuery') {
    // if (message.length > 500) {
    //   addMessage('system', "Your message is a bit long. Could you make it a little shorter?");
    //   return;
    // }
    addMessage('user', message);
    setUserData(prev => ({ ...prev, query: message }));
    setInputValue('');
    addMessage('agent', "Got it! Which country are you contacting us from?");
    setConversationStage('collectCountry');

  // Stage 5 - Country
  } else if (conversationStage === 'collectCountry') {
    if (message.length < 2) {
      addMessage('system', "That doesn't seem like a valid country name. Could you try again?");
      return;
    }
    addMessage('user', message);
    setUserData(prev => ({ ...prev, country: message }));
    setInputValue('');

    addMessage('agent', "Before we proceed, I need to ask you a couple of quick questions:");
    addMessage('agent', "Do you consent to being contacted by GenAIPulse in response to your inquiry?");
    setShowConsentOptions(true);
    setConversationStage('collectConsent');

  // Stage 6 - Consent Contact
  } else if (conversationStage === 'collectConsent') {
    const normalizedMessage = message.toLowerCase();

    if (normalizedMessage === 'yes' || normalizedMessage === 'y') {
      addMessage('user', 'Yes');
      setUserData(prev => ({ ...prev, consentContact: true }));
    } 
    // else if (normalizedMessage === 'no' || normalizedMessage === 'n') {
    //   addMessage('user', 'No');
    //   setUserData(prev => ({ ...prev, consentContact: false }));
    // }
     else {
      addMessage('system', "this question is mandatory to accept to move forward.");
      return;
    }

    setInputValue('');
    addMessage('agent', "Thank you! One last question:");
    addMessage('agent', "Would you like to receive marketing news and updates from GenAIPulse? (Please answer 'yes' or 'no')");
    setConversationStage('collectMarketing');

  // Stage 7 - Marketing Updates + Final Submission
  } else if (conversationStage === 'collectMarketing') {
    const normalizedMessage = message.toLowerCase();
    let updatedData;

    if (normalizedMessage === 'yes' || normalizedMessage === 'y') {
      addMessage('user', 'Yes');
      updatedData = { ...userData, marketingUpdates: true };
    } else if (normalizedMessage === 'no' || normalizedMessage === 'n') {
      addMessage('user', 'No');
      updatedData = { ...userData, marketingUpdates: false };
    } else {
      addMessage('system', "Please answer with 'yes' or 'no'.");
      return;
    }

    setUserData(updatedData);
    setInputValue('');

    try {
      await submitUserData(updatedData); // ✅ ensures latest data is sent
      addMessage('agent', "Your query was submitted successfully. We shall be in touch with you shortly!");
      setConversationStage('completed');
      setShowConsentOptions(false);
    } catch (error) {
      addMessage('agent', "We encountered an issue submitting your enquiry. Please try again later.");
      console.error('Submission error:', error);
    }

  // Stage 8 - Normal chat after data collection
  } else if (conversationStage === 'completed') {
    addMessage('user', message);
    setInputValue('');

    setMessages(prev => [...prev, { type: 'agent', text: '...', isTyping: true }]);

    try {
      const response = await processUserInput(message);
      setMessages(prev => prev.filter(msg => !msg.isTyping));
      addMessage('agent', response.text);
    } catch (error) {
      setMessages(prev => prev.filter(msg => !msg.isTyping));
      addMessage('agent', "Sorry, I'm having trouble processing your request. Please try again later.");
      console.error('Error processing query:', error);
    }
  }
};


  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Call OpenAI API
  const callOpenAI = async (userMessage: string) => {
    try {
      const response = await fetch(`${baseUrl}/api/llm`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          userMessage: userMessage
        })
      });

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }

      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Error calling OpenAI API:', error);
      throw error;
    }
  };

  // Get placeholder text
  const getPlaceholder = () => {
    switch (conversationStage) {
      case 'initial': return 'Type your message...';
      case 'collectEmail': return 'Enter your email address...';
      case 'collectMobile': return 'Enter your mobile number...';
      case 'collectQuery': return 'Enter your query...';
      case 'collectCountry': return 'Enter your country...';
      case 'collectConsent': 
      case 'collectMarketing': return 'Type yes or no...';
      case 'completed': return 'Ask any follow-up questions...';
      default: return 'Type your message...';
    }
  };

  // Quick response buttons for consent questions
 // Add this new function to handle consent responses directly
const handleConsentResponse = async (response: 'yes' | 'no') => {
  if (conversationStage === 'collectConsent') {
    addMessage('user', response === 'yes' ? 'Yes' : 'No');
    setUserData(prev => ({ ...prev, consentContact: response === 'yes' }));
    
    addMessage('agent', "Thank you! One last question:");
    addMessage('agent', "Would you like to receive marketing news and updates from GenAIPulse? (Please answer 'yes' or 'no')");
    setConversationStage('collectMarketing');
    
  } else if (conversationStage === 'collectMarketing') {
    addMessage('user', response === 'yes' ? 'Yes' : 'No');
    const updatedData = { ...userData, marketingUpdates: response === 'yes' };
    setUserData(updatedData);

    try {
      await submitUserData(updatedData);
      addMessage('agent', "Your query was submitted successfully. We shall be in touch with you shortly!");
      setConversationStage('completed');
      setShowConsentOptions(false);
    } catch (error) {
      addMessage('agent', "We encountered an issue submitting your enquiry. Please try again later.");
      console.error('Submission error:', error);
    }
  }
};

// Update the renderQuickResponseButtons function
const renderQuickResponseButtons = () => {
  if (showConsentOptions) {
    return (
      <div className="flex space-x-2 mt-2">
        <button
          onClick={() => handleConsentResponse('yes')}
          className="px-3 py-1 bg-green-500 text-white rounded-md text-sm"
        >
          Yes
        </button>
        {
          conversationStage != 'collectConsent'
           &&
            <button
          onClick={() => handleConsentResponse('no')}
          className="px-3 py-1 bg-red-500 text-white rounded-md text-sm"
        >
          No
        </button>
        }
       
      </div>
    );
  }
  return null;
};

  console.log(messages,"messages")

  return (
    <div className="fixed bottom-5 right-4 z-50">
      <div className="relative">
        {/* Chatbox */}
        <div className={`flex flex-col w-80 h-[500px] bg-white rounded-lg shadow-lg overflow-hidden ${isChatVisible ? 'flex' : 'hidden'}`}>
          {/* Header */}
          <div className="bg-gray-50 p-3 border-b border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center font-semibold">
                <span className="mr-2">💬</span>
                <span>Chat</span>
              </div>
              <button 
                className="text-gray-500 hover:text-gray-700 text-xl"
                onClick={toggleChat}
              >
                ✕
              </button>
            </div>

            {/* Agent info */}
            <div className="flex items-center py-2">
              <img
                src={'/Chatbot.jpg'}
                alt="Agent"
                className="w-10 h-10 rounded-full mr-3"
              />
              <div className="flex-grow">
                <p className="text-sm font-medium">{agent.name}</p>
              </div>
              <button className="text-gray-500 text-xl">⋮</button>
            </div>
          </div>

          {/* Messages */}
          <div 
            ref={chatMessagesRef}
            className="flex-1 overflow-y-auto p-4 bg-white"
          >
            {messages.map((msg, index) => (
              <div 
                key={index}
                className={`mb-3 p-3 rounded-lg max-w-[80%] ${
                  msg.type === 'user' ? 'ml-auto bg-blue-500 text-white' : 
                  msg.type === 'agent' ? 'mr-auto bg-gray-100 text-gray-800' : 
                  'mr-auto bg-yellow-100 text-yellow-800'
                }`}
              >
                {msg.type === 'agent' && !msg.isTyping && (
                  <img
                src={'/Chatbot.jpg'}
                alt="Agent"
                className="w-10 h-10 rounded-full mr-3"
              />
                )}
                {msg.text}
                {index === messages.length - 1 && msg.type === 'agent' && renderQuickResponseButtons()}
              </div>
            ))}
          </div>

          {/* Input area */}
          <div className="p-3 border-t border-gray-200">
            <div className="flex">
              <textarea
                ref={messageInputRef}
                value={inputValue}
                readOnly = {conversationStage == "completed"}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={getPlaceholder()}
                rows={1}
                className="flex-grow p-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              <button
                onClick={sendMessage}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 rounded-r-lg"
              >
                ➤
              </button>
            </div>
          </div>
        </div>

        {/* Chat trigger button */}
       <button
 className={`absolute bottom-0 right-0 w-12 h-12 bg-blue-500 rounded-full shadow-lg flex items-center justify-center transition-all ${isChatVisible ? 'opacity-0 scale-0' : 'opacity-100 scale-100'} animate-float`}  onClick={toggleChat}
>
  {/* Notification badge */}
  <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
    1
  </div>
  
  <img
    src="/chatbot.png"
    alt="Chat"
    className="w-8 h-8"
  />
</button>
      </div>

      {/* Audio element */}
      {/* <audio id="chatNotification" preload="auto"  ref={audioRef}>
        <source
          src="https://commondatastorage.googleapis.com/codeskulptor-assets/Collision8-Bit.ogg"
          type="audio/ogg"
        />
        <source
          src="https://cdn.freesound.org/previews/615/615096_12540845-lq.ogg"
          type="audio/ogg"
        />
      </audio> */}

      
    </div>
  );
}